package com.example.monetix.utils

import androidx.compose.ui.text.input.TextFieldValue
import com.example.monetix.db.FinancingEntity // <-- Aggiornato per SQLDelight
import androidx.compose.ui.text.TextRange
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

fun autoFormatDateInput(raw: String): String {
    val digits = raw.filter { it.isDigit() }.take(8)
    return buildString {
        digits.forEachIndexed { index, c ->
            append(c)
            if ((index == 1 || index == 3) && index != digits.lastIndex) append('/')
        }
    }
}

// 🔥 Parsing KMP puro: Niente DateTimeFormatter Java!
fun parseDayMonthYearToEpochDayOrNull(raw: String): Long? {
    val normalized = raw.trim()
    if (normalized.isBlank()) return null

    return try {
        val parts = normalized.split("/")
        if (parts.size == 3) {
            val day = parts[0].toInt()
            val month = parts[1].toInt()
            val year = parts[2].toInt()
            LocalDate(year, month, day).toEpochDays().toLong()
        } else {
            null
        }
    } catch (_: Throwable) {
        null
    }
}

// 🔥 Formattazione KMP pura (dd/MM/yyyy)
fun formatEpochDayToItalianDate(epochDay: Long): String {
    val date = LocalDate.fromEpochDays(epochDay.toInt())
    val dayStr = date.dayOfMonth.toString().padStart(2, '0')
    val monthStr = date.monthNumber.toString().padStart(2, '0')
    return "$dayStr/$monthStr/${date.year}"
}

fun replaceSelectedPrefilledDateIfNeeded(
    currentText: String,
    selectedEpochDay: Long?
): String {
    return selectedEpochDay?.let { formatEpochDayToItalianDate(it) } ?: currentText
}

fun replaceSelectedPrefilledDateIfNeeded(
    oldValue: TextFieldValue,
    incoming: TextFieldValue
): TextFieldValue {
    val fullDateRegex = Regex("\\d{2}/\\d{2}/\\d{4}")
    val wholeTextWasSelected =
        oldValue.text.matches(fullDateRegex) &&
                oldValue.selection.start == 0 &&
                oldValue.selection.end == oldValue.text.length

    if (!wholeTextWasSelected) return nextDateFieldValue(oldValue, incoming)

    // se l'utente tocca il campo e il testo è ancora identico → svuota
    if (incoming.text == oldValue.text) {
        return TextFieldValue("")
    }

    val typedDigits = incoming.text.filter { it.isDigit() }
    if (typedDigits.isEmpty()) {
        return TextFieldValue("")
    }

    return nextDateFieldValue(
        TextFieldValue(""),
        TextFieldValue(typedDigits)
    )
}

fun monthsBetweenInclusive(startEpochDay: Long, endEpochDay: Long): Int {
    val start = LocalDate.fromEpochDays(startEpochDay.toInt())
    val end = LocalDate.fromEpochDays(endEpochDay.toInt())
    // Formula matematica senza ChronoUnit
    val months = (end.year - start.year) * 12 + (end.monthNumber - start.monthNumber)
    return months + 1
}

// Helper per sapere quanti giorni ha un mese (sostituisce YearMonth.lengthOfMonth di Java)
fun lengthOfMonth(year: Int, month: Int): Int {
    val isLeap = (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)
    return when (month) {
        2 -> if (isLeap) 29 else 28
        4, 6, 9, 11 -> 30
        else -> 31
    }
}

// 🔥 Logica avanzata rate riscritta senza YearMonth!
fun dueInstallmentsUntilToday(
    f: FinancingEntity,
    today: LocalDate = Clock.System.todayIn(TimeZone.currentSystemDefault())
): Int {
    val start = LocalDate.fromEpochDays(f.startEpochDay.toInt())
    val end = LocalDate.fromEpochDays(f.endEpochDay.toInt())

    // In KMP possiamo usare gli operatori di comparazione diretti (<, <=) al posto di isBefore()
    if (end < start) return 0
    if (today < start) return 0

    val last = if (today < end) today else end

    var currentYear = start.year
    var currentMonth = start.monthNumber
    var count = 0

    val endYear = last.year
    val endMonth = last.monthNumber

    while (currentYear < endYear || (currentYear == endYear && currentMonth <= endMonth)) {
        val maxDays = lengthOfMonth(currentYear, currentMonth)
        val day = start.dayOfMonth.coerceAtMost(maxDays)

        val dueDate = LocalDate(currentYear, currentMonth, day)
        if (dueDate <= last) count++

        currentMonth++
        if (currentMonth > 12) {
            currentMonth = 1
            currentYear++
        }
    }
    return count
}
/**
 * Estensione per rimpiazzare java.time.YearMonth.lengthOfMonth() in KMP.
 * Calcola i giorni del mese gestendo correttamente gli anni bisestili.
 */
val kotlinx.datetime.LocalDate.lengthOfMonth: Int
    get() {
        return lengthOfMonth(year, monthNumber)
    }

/**
 * Versione funzione per compatibilità con chiamate tipo .lengthOfMonth()
 */
fun kotlinx.datetime.LocalDate.lengthOfMonth(): Int {
    return this.lengthOfMonth
}
/**
 * Gestisce l'evoluzione del TextField per la data,
 * applicando la maschera dd/mm/yyyy e spostando il cursore.
 */
fun nextDateFieldValue(oldValue: TextFieldValue, newValue: TextFieldValue): TextFieldValue {
    val digits = newValue.text.filter { it.isDigit() }.take(8)
    val formatted = autoFormatDateInput(digits)

    // Se l'utente sta cancellando, non forzare il cursore alla fine
    val isDeleting = newValue.text.length < oldValue.text.length
    val selection = if (isDeleting) newValue.selection else TextRange(formatted.length)

    return newValue.copy(
        text = formatted,
        selection = selection
    )
}